import React from 'react';
import Banner from '../../../common/Banner';
import type { PADeadlines, PAFlowSheetError, PAPacketResponse, PASubmitWarning } from '../../../../dtos/patientmr/authorizations.dto';
import { AuthorizationsService } from '../../../../services/patientmr/authorizations.service';

const FLOWSHEET_ERROR_LABELS: Record<string, string> = {
  NOT_LINKED: 'No F00120 flow sheet is linked to this PA',
  FLOWSHEET_NOT_LINKED: 'No F00120 flow sheet is linked to this PA',
  NOT_FINALIZED: 'The linked F00120 is not finalized',
  FLOWSHEET_NOT_FINALIZED: 'The linked F00120 is not finalized',
  MISSING_CODE: 'care is entered without a caregiver code',
  MISSING_CAREGIVER: 'care is entered without a caregiver code',
  UNKNOWN_CODE: 'unknown caregiver code (use N, O, P, Q or S)',
  INVALID_CODE: 'unknown caregiver code (use N, O, P, Q or S)',
  EMPTY_DAY: 'no coded cells for this day',
  NO_CODED_CELLS: 'no coded cells for this day',
};

export function describeFlowSheetError(e: PAFlowSheetError): string {
  const where = [e.day, e.time].filter(Boolean).join(' ');
  const what = FLOWSHEET_ERROR_LABELS[e.code] || e.code.replace(/_/g, ' ').toLowerCase();
  return where ? `${where}: ${what}` : what;
}

/** 422 FLOWSHEET_INCOMPLETE: submit is blocked until the linked F00120 is complete (§3.6a). */
export const FlowSheetErrorsBanner: React.FC<{ errors: PAFlowSheetError[]; onDismiss: () => void }> = ({ errors, onDismiss }) => (
  <Banner variant="error" title="The F00120 flow sheet is incomplete" onDismiss={onDismiss} className="mx-4 mt-4" data-testid="flowsheet-incomplete">
    <p>Fix these in the linked F00120 (Forms), then submit again.</p>
    {errors.length > 0 && (
      <ul className="mt-1 list-disc list-inside" aria-label="Flow sheet gaps">
        {errors.map((e, i) => <li key={`${e.day}-${e.time || ''}-${e.code}-${i}`}>{describeFlowSheetError(e)}</li>)}
      </ul>
    )}
  </Banner>
);

/** Body of the submit confirmation dialog (PA-11/12/13 warnings never block). */
export const SubmitWarningsList: React.FC<{ warnings: PASubmitWarning[] }> = ({ warnings }) => (
  <div>
    <p>Review before submitting. These don't block submission:</p>
    <ul className="mt-2 list-disc list-inside space-y-1" aria-label="Submit warnings">
      {warnings.map((w, i) => <li key={`${w.code}-${i}`}>{w.message}</li>)}
    </ul>
  </div>
);

/** PA-11 deadline helpers on a DRAFT PA; renders only what the server returned. */
export const DeadlinesBanner: React.FC<{ deadlines?: PADeadlines | null }> = ({ deadlines }) => {
  const initial = deadlines?.initialDueDate;
  const recert = deadlines?.recertWindow;
  if (!initial && !(recert?.start && recert?.end)) return null;
  return (
    <Banner variant="info" title="PA deadlines" className="mx-4 mt-4" data-testid="pa-deadlines">
      <ul className="list-disc list-inside">
        {initial && <li>Initial PA due by {AuthorizationsService.formatDate(initial)}</li>}
        {recert?.start && recert?.end && (
          <li>Recertification window: {AuthorizationsService.formatDate(recert.start)} &ndash; {AuthorizationsService.formatDate(recert.end)}</li>
        )}
      </ul>
    </Banner>
  );
};

/** Packet result (included forms) or the 422 naming what's missing. */
export const PacketBanner: React.FC<{
  result: PAPacketResponse | null;
  error: { message: string; missing: string[] } | null;
  onDismiss: () => void;
}> = ({ result, error, onDismiss }) => {
  if (error) {
    return (
      <Banner variant="error" title="Packet not generated" onDismiss={onDismiss} className="mx-4 mt-4" data-testid="packet-error">
        <p>{error.message}</p>
        {error.missing.length > 0 && (
          <ul className="mt-1 list-disc list-inside" aria-label="Missing for packet">
            {error.missing.map(m => <li key={m}>{m}</li>)}
          </ul>
        )}
      </Banner>
    );
  }
  if (!result) return null;
  return (
    <Banner
      variant="info"
      title={`Packet v${result.version} generated`}
      onDismiss={onDismiss}
      className="mx-4 mt-4"
      data-testid="packet-result"
      action={
        <a href={result.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium underline">
          Download
        </a>
      }
    >
      {result.forms.length > 0 ? <>Includes: {result.forms.join(', ')}</> : 'The PDF opened in a new tab.'}
    </Banner>
  );
};
